import { useEffect, useState, useCallback } from 'react';
import classNames from 'classnames';

import Choice from '../../../../types/DecisionTree/Choice';
import Node from '../../../../types/DecisionTree/Node';
import Path from '../../../../types/DecisionTree/Path';

import Content from '../Content/Content';
import {
    sortByOrderAsc,
    scrollElementIntoViewOnMobile,
    getStatisticsUrlForDecisionTreeDecision,
    getStatisticsImg
} from '../../../../utils';

const DecisionTree = ({ decisionTree, setLastStep }) => {
    const [path, setPath] = useState(
        new Path(
            [new Node({ stepId: decisionTree.root })],
            (stepId) => decisionTree.steps[stepId]
        )
    );
    const [steps, setSteps] = useState(path.toSteps());
    const [statisticsUrl, setStatisticsUrl] = useState('');
    const [choiceTarget, setChoiceTarget] = useState(null);

    const handleStatistics = (uid) => setStatisticsUrl(getStatisticsUrlForDecisionTreeDecision(uid));
    const getLastStep = steps => steps?.slice(-1)[0];
    const getLastStepAndCallBack = useCallback((steps) => {
        const lastStep = getLastStep(steps);
        if (!lastStep) {
            return;
        }
        setLastStep(lastStep);
    }, [setLastStep]);

    useEffect(() => {
        getLastStepAndCallBack(steps);

        if (choiceTarget) {
            scrollElementIntoViewOnMobile(choiceTarget);
        }
    }, [steps, getLastStepAndCallBack, choiceTarget]);

    const updatePathAndSteps = (choice) => {
        const updatedPath = path
            .setSelectedChoice(choice)
            .truncateUnselectedSteps(choice)
            .addTargetStep(choice);
        setPath(updatedPath);
        const updatedSteps = updatedPath.toSteps();
        getLastStepAndCallBack(updatedSteps);
        setSteps(updatedSteps);
    };

    const getTitle = (step) =>
        step.title && <h2 data-cy="entry-step-title">{step.title}</h2>;

    const getContent = (step) =>
        step.content && <Content content={step.content} />;

    const getChoices = (step) => {
        if (!step?.choices) {
            return null;
        }

        const sortedChoices = sortByOrderAsc(step.choices);
        const sortedChoicesButtonElements = sortedChoices.map((choice) => (
            <button
                key={choice.id}
                aria-describedby={choice.target}
                role="tab"
                className={
                    classNames(
                        'btn entry__step-navigation-btn', 
                        path.isChosen(step.id, choice.id) ? 'active' : 'btn--secondary'
                    )}
                aria-selected={path.isChosen(step.id, choice.id)}
                onClick={(e) => {
                    e.preventDefault();
                    updatePathAndSteps(new Choice({ stepId: step.id, ...choice }));
                    setChoiceTarget(choice.target); // TODO: Add UI tests
                    handleStatistics(choice.uid);
                }}
            >
                {choice.title}
            </button>
        ));

        return (
            <div
                data-cy="entry-step-navigation"
                className="entry__step-navigation"
                role="tablist"
            >
                {sortedChoicesButtonElements}
            </div>
        );
    };

    return (
        <>
            {getStatisticsImg(statisticsUrl)}
            {steps.map((step) => (
                <div
                    id={step.id}
                    name={step.id}
                    key={step.id}
                    data-cy="entry-step-wrapper"
                    className="entry__step-wrapper"
                    role="tabpanel"
                >
                    {getTitle(step)}
                    {getContent(step)}
                    {getChoices(step)}
                </div>
            ))}
        </>
    );
};

export default DecisionTree;
